<script setup lang="ts">
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import type {MoodDTO} from '@/ts/types/dto/journals-dto';
import type {JournalFeelingOption} from '@/ts/types/component/today-page';

/**
 * The feelings step of a journal entry: what is chosen, a search, and the suggestions left to pick
 * (IPD-3107).
 *
 * Extracted from the Today tile's entry form so the Journal tab's own modal offers the same thing.
 * The modal used to carry a typeahead and a "+" that opened the whole catalogue in a popup, which
 * asked the user to already know the word they wanted and never related it to the mood they had
 * just picked.
 *
 * Suggestions are keyed by mood by the caller: what is offered after Joyful should not be what is
 * offered after Sad. Only secondary feelings are ever offered — the six primary families are the
 * grouping, and one of them is already implied by the mood.
 *
 * Surfaces come from the `--ef-*` locals when this sits inside the Today form, and fall back to
 * the design tokens when it stands on its own in a dialog.
 */
const props = defineProps<{
  /** Secondary feelings keyed by mood. */
  feelingsByMood?: Record<string, JournalFeelingOption[]>;
  /** Offered for a mood with no family of its own, and before a mood is chosen. */
  fallbackFeelings?: JournalFeelingOption[];
  /** The mood the entry carries, which decides which suggestions are shown. */
  mood?: MoodDTO | null;
  /**
   * Whether to draw a close button beside the search. The popover host needs one; the Today form,
   * where the picker is simply the last step of the mask, has nothing to close.
   */
  dismissible?: boolean;
}>();

const emit = defineEmits<{dismiss: []}>();

const selected = defineModel<JournalFeelingOption[]>({default: () => []});
/**
 * Bound rather than held here, so the host can drop it with the rest of the draft. Reaching into
 * the child for a `reset()` worked until anything stubbed the child.
 */
const search = defineModel<string>('search', {default: ''});

const {t} = useI18n();

/** "Feelings similar to Joyful" once a mood is chosen, the common set before that. */
const groupLabel = computed(() => (props.mood
  ? t('journal_tile_feelings_similar', {mood: t(`mood_${props.mood}`)})
  : t('journal_tile_feelings_common')));

const available = computed(() => {
  const chosen = new Set(selected.value.map((feeling) => feeling.id));
  const forMood = props.mood ? props.feelingsByMood?.[props.mood] : null;
  const source = forMood ?? props.fallbackFeelings ?? [];
  const seen = new Set<string>();

  // A feeling can sit under more than one family, so the same word arrives twice. De-duplicated by
  // name rather than id: the ids differ, and two identical pills read as a rendering fault.
  const pool = source.filter((feeling) => {
    if (chosen.has(feeling.id) || seen.has(feeling.name)) {
      return false;
    }
    seen.add(feeling.name);
    return true;
  });

  const query = search.value.trim().toLowerCase();
  if (!query) {
    return pool;
  }
  return pool.filter((feeling) => feeling.name.toLowerCase().includes(query));
});

function toggleFeeling(feeling: JournalFeelingOption): void {
  selected.value = selected.value.some((chosen) => chosen.id === feeling.id)
    ? selected.value.filter((chosen) => chosen.id !== feeling.id)
    : [...selected.value, feeling];
}

</script>

<template>
  <div class="ef-feelings-picker">
    <div v-if="selected.length > 0" class="ef-feel-field">
      <span
        v-for="feeling in selected"
        :key="feeling.id"
        class="ef-feel-tag"
      >
        {{ feeling.name }}
        <button
          type="button"
          class="x"
          :aria-label="$t('journal_tile_remove_feeling', {feeling: feeling.name})"
          @click="toggleFeeling(feeling)"
        ><svg
          width="8"
          height="8"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="round"
          aria-hidden="true"
        ><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg></button>
      </span>
    </div>

    <div class="ef-feel-searchrow">
      <input
        v-model="search"
        class="ef-feel-search"
        type="text"
        :placeholder="$t('journal_tile_feelings_search')"
        :aria-label="$t('journal_tile_feelings_search')"
      />
      <button
        v-if="dismissible"
        type="button"
        class="ef-feel-dismiss"
        :aria-label="$t('close')"
        @click="emit('dismiss')"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.6"
          stroke-linecap="round"
          aria-hidden="true"
        ><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
      </button>
    </div>

    <div class="ef-feel-groups">
      <div v-if="available.length > 0" class="ef-feel-group">
        <div class="ef-feel-group-lab">
          {{ groupLabel }}
        </div>
        <div class="ef-feelings-grid">
          <button
            v-for="feeling in available"
            :key="feeling.id"
            type="button"
            class="ef-feeling"
            @click="toggleFeeling(feeling)"
          >
            {{ feeling.name }}
          </button>
        </div>
      </div>
      <div v-else class="ef-feel-empty">
        {{ $t('journal_tile_feelings_empty') }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ef-feelings-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
}

// The chosen feelings sit in a field of their own, 16px cornered against the search's full pill.
// Without the background the tags floated loose on the card.
.ef-feel-field {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  background: var(--ef-field, var(--Neutral-UI-neutral-ui-10));
  border-radius: 16px;
  padding: 8px 10px;
}

.ef-feel-tag {
  display: inline-flex;
  align-items: center;
  // 6 and 6/8/6/12, from the prototype: the label needs more room on its left than the remove
  // control needs on its right, or the pill reads lopsided.
  gap: 6px;
  font-weight: 600;
  font-size: 12px;
  // Pinned: the tag inherits a 25.6px line box, which makes the pill 33.6 tall against the 30 the
  // prototype draws.
  line-height: 18px;
  // `--ef-surface`, not `--ef-on-ink`: the Today form re-points the former inside its dark block
  // and not the latter, so swapping them moved the pill's text a step in dark mode.
  color: var(--ef-surface, var(--Neutral-UI-neutral-ui-0));
  background: var(--ef-ink, var(--Neutral-UI-neutral-ui-100));
  border-radius: 100px;
  padding: 6px 8px 6px 12px;
  text-transform: capitalize;

  .x {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    // Zeroed so the 8px icon stays centred in the 16px circle; the button's own inline padding
    // squeezed it.
    padding: 0;
    border-radius: 100px;
    background: rgb(255 255 255 / 18%);
    color: inherit;
    border: 0;
    cursor: pointer;
    line-height: 1;
    transition: background 150ms ease;

    &:hover {
      background: rgb(255 255 255 / 36%);
    }
  }
}

// The search and its close button share a row, as the design draws them.
.ef-feel-searchrow {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ef-feel-dismiss {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 100px;
  cursor: pointer;
  background: var(--ef-field, var(--Neutral-UI-neutral-ui-10));
  color: var(--ef-ink-2, var(--Neutral-UI-neutral-ui-70));
  transition: background 150ms ease;

  &:hover {
    background: color-mix(in srgb, var(--ef-field, var(--Neutral-UI-neutral-ui-10)) 88%, var(--ef-ink, var(--Neutral-UI-neutral-ui-100)));
  }
}

.ef-feel-search {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 500;
  // Floored at 16px so iOS does not zoom the field on focus (IPD-3064).
  font-size: max(16px, var(--Typography-Body));
  line-height: 18px;
  color: var(--ef-ink, var(--Neutral-UI-neutral-ui-100));
  background: var(--ef-field, var(--Neutral-UI-neutral-ui-10));
  border: 0;
  border-radius: 100px;
  padding: 8px 16px;
  outline: none;

  &::placeholder {
    color: var(--ef-ink-3, var(--Neutral-UI-neutral-ui-50));
  }

  &:focus {
    box-shadow: inset 0 0 0 1.5px rgb(169 197 195 / 70%);
  }
}

.ef-feel-groups {
  display: flex;
  flex-direction: column;
  gap: 12px;
  // The catalogue runs to a few hundred pills, so the list needs a ceiling or it stretches its
  // host. 285 is what the Today tile's wide band resolves to, and it leaves every band usable.
  flex: 1 1 auto;
  min-height: 0;
  max-height: 285px;
  overflow-y: auto;
  scrollbar-width: thin;
}

.ef-feel-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ef-feel-group-lab {
  font-weight: 600;
  font-size: 10px;
  line-height: 15px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  // The design's own label grey, which sits between ui-50 and ui-70 and is on neither.
  color: var(--ef-label, #58585a);
}

.ef-feelings-grid {
  display: flex;
  flex-wrap: wrap;
  // 8, measured off the prototype's own chip row. At 4 the pills read as one block of text rather
  // than as separate things to tap.
  gap: 8px;
}

.ef-feeling {
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: var(--ef-ink, var(--Neutral-UI-neutral-ui-100));
  background: var(--ef-field, var(--Neutral-UI-neutral-ui-10));
  border: 0;
  border-radius: 100px;
  padding: 8px 16px;
  cursor: pointer;
  text-transform: capitalize;
  transition: background 150ms ease;

  &:hover {
    background: rgb(169 197 195 / 32%);
  }
}

.ef-feel-empty {
  font-weight: 500;
  font-size: 12px;
  color: var(--ef-ink-2, var(--Neutral-UI-neutral-ui-70));
  opacity: 0.7;
  padding: 4px 2px;
}
</style>
